import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpHeaders} from '@angular/common/http';

import {Observable} from 'rxjs/observable';
import {forkJoin} from 'rxjs/observable/forkJoin';
import 'rxjs/add/observable/of';

import {Subject} from 'rxjs/Subject';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/finally';

import {environment} from '../../../environments/environment';

import {TreeNode} from 'primeng/primeng';
import {Equipment} from '../../shared/models/equipment';

import {DataService} from './data.service';
import {SpinnerService} from './spinner.service';
import {AlertService} from './alert.service';
import {TranslateService} from '@ngx-translate/core';
import {OfferEntryData} from '../models/offerEntryData';

import {EquipmentGroup} from '../../shared/models/equipment-group';

import {UtilsService} from './utils.service';

@Injectable()
export class EquipmentService {
    useMockData: boolean = false;

    groups: Subject<any> = new BehaviorSubject(null);
    nonGroupedEquipment: Subject<any> = new BehaviorSubject(null);

    equipments: Subject<any> = new BehaviorSubject(null);

    constructor(
        private data: DataService,
        private spinnerService: SpinnerService,
        private alertService: AlertService,
        private translateService: TranslateService,
        private utilsService: UtilsService
    ) {
    }

    init() {
        if (this.groups) {
            this.groups.next(null);
        } else {
            this.groups = new BehaviorSubject<OfferEntryData>(null);
        }

        if (this.nonGroupedEquipment) {
            this.nonGroupedEquipment.next(null);
        } else {
            this.nonGroupedEquipment = new BehaviorSubject<OfferEntryData>(null);
        }

        if (this.equipments) {
            this.equipments.next(null);
        } else {
            this.equipments = new BehaviorSubject<OfferEntryData>(null);
        }

        const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
        if (currentUser) {
            this.loadData();
        } else {
            this.utilsService.debugLog('cannot load userinfo');
        }
    }

    private getHeader(): HttpHeaders {
        let headers = new HttpHeaders();
        const programId = sessionStorage.getItem('currentProgram');
        this.utilsService.debugLog(programId);
        if (programId) {
            headers = headers.set('Program', programId);
        }

        return headers;
    }

    addEquipmentToGroup(portfolioId: string, groupId: string, equipmentList: any[]): Observable<any> {
        this.spinnerService.show('offerSpinner');

        const bodyList = this.setBodyForGroupingEquipment('ADDREGISTRATIONTOGROUP', portfolioId, groupId, equipmentList);
        const endpoint = `${environment.equipment_url}/grouping/v1`;
        this.utilsService.debugLog('add equipment to group', bodyList);
        this.utilsService.debugLog('equipmentList', equipmentList);
        const httpRequests = [];
        for (const body of bodyList) {
            httpRequests.push(this.data.post(endpoint, body, this.getHeader())
                .map((response) => ({
                    success: true,
                    response: response,
                    portfolioId: body.portfolio_id,
                    groupId: body.group_id,
                    equipmentId: body.oms_equipment_grouping_id,
                    equipmentName: equipmentList.filter((data) => (data.id === body.oms_equipment_grouping_id))[0].displayedName,
                })).catch(error => (Observable.of({
                    success: false,
                    response: error,
                    portfolioId: body.portfolio_id,
                    groupId: body.group_id,
                    equipmentId: body.oms_equipment_grouping_id,
                    equipmentName: equipmentList.filter((data) => (data.id === body.oms_equipment_grouping_id))[0].displayedName,
                })))
            );
        }
        return forkJoin(httpRequests).map((response) => {
            // this.utilsService.debugLog(response);
            if (Array.isArray(response) && response.length > 0) {
                if (response.filter((res: any) => (res.success === true)).length > 0) {
                    this.loadData();
                }
                return response;
            }
        }).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }

    removeEquipmentFromGroup(portfolioId: string, groupId: string, equipmentId: string): Observable<any> {
        this.spinnerService.show('offerSpinner');

        const endpoint = `${environment.equipment_url}/grouping/v1`;
        const body = this.setBodyForGroupingEquipment(
            'REMOVEREGISTRATIONFROMGROUP', portfolioId, groupId, [{id: equipmentId}]
        )[0];

        return this.data.post(endpoint, body, this.getHeader()).map((response) => {
            this.loadData();
            return {
                success: true,
                response: response,
                portfolioId: body.portfolio_id,
                groupId: body.group_id,
                equipmentId: body.oms_equipment_grouping_id
            };
        }).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }

    private setBodyForGroupingEquipment(actionType: string, portfolioId: string, groupId: string, equipmentList: any[]): any[] {
        const returnBody: any[] = [];

        for (const equipment of equipmentList) {
            const body = {
                action_type: actionType,
                portfolio_id: portfolioId,
                group_id: groupId,
                oms_equipment_grouping_id: equipment.id
            };

            returnBody.push(body);
        }
        return returnBody;
    }

    createGroup(group: EquipmentGroup): Observable<any> {
        this.spinnerService.show('offerSpinner');
        const body = this.setBodyForGroup('CREATEGROUP', group);
        const endpoint = `${environment.equipment_url}/grouping/v1`;

        return this.data.post(endpoint, body, this.getHeader()).map((response) => {
            this.loadData();
            return response;
        }).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }

    updateGroup(group: EquipmentGroup): Observable<any> {
        this.spinnerService.show('offerSpinner');
        const body = this.setBodyForGroup('UPDDATEGROUP', group);
        const endpoint = `${environment.equipment_url}/grouping/v1`;

        return this.data.post(endpoint, body, this.getHeader()).map((response) => {
            this.loadData();
            return response;
        }).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }

    deleteGroup(group: EquipmentGroup): Observable<any> {
        this.spinnerService.show('offerSpinner');
        const body = {
            action_type: 'DELETEGROUP',
            portfolio_id: group.portfolioId,
            group_id: group.id
        };
        const endpoint = `${environment.equipment_url}/grouping/v1`;

        return this.data.post(endpoint, body, this.getHeader()).map((response) => {
            this.loadData();
            return response;
        }).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }

    private setBodyForGroup(actionType: string, group: EquipmentGroup): any {
        const body = {
            action_type: actionType,
            portfolio_id: group.portfolioId,
            start_datetime: this.getDateAsFormatedString(group.startDatetime),
            group_code: group.code,
            group_id: group.id,
            group_name_en: group.nameEn,
            group_name_local: group.nameLocal,
            group_derating_factor: group.deratingFactor
        };

        if (group.endDatetime) {
            body['end_datetime'] = this.getDateAsFormatedString(group.endDatetime); // Note this is optional if not provided, it will be set to null
        }

        return body;
    }

    addEquipmentToPortfolio(offerEntryData: OfferEntryData): Observable<any> {
        this.spinnerService.show('offerSpinner');

        const bodyList = this.setBodyForaddEquipmentToPortfolio(offerEntryData);
        const endpoint = environment.offer_equipment_url;
        this.utilsService.debugLog('add equipment to portfolio');

        const httpRequests = [];
        for(const body of bodyList) {
            httpRequests.push(this.data.post(endpoint, body, this.getHeader())
                .map((response) => {
                    return {
                        success: true,
                        response: response,
                        portfolioId: body.portfolio_id,
                        equipmentId: body.equipment_id,
                        equipmentName: offerEntryData.gridRows.filter((data) => (data.id === body.equipment_id))[0].name,
                        startDate: body.start_datetime,
                        endDate: body.end_datetime,
                        product: body.product
                    };
                }).catch(error => (Observable.of({
                    success: false,
                    response: error,
                    portfolioId: body.portfolio_id,
                    equipmentId: body.equipment_id,
                    equipmentName: offerEntryData.gridRows.filter((data) => (data.id === body.equipment_id))[0].name,
                    startDate: body.start_datetime,
                    endDate: body.end_datetime,
                    product: body.product
                }))
            ));
        }
        return forkJoin(httpRequests).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }

    removeEquipmentFromPortfolio(portfolioId: string, equipmentLevel: string, equipmentId: string, startDate: string, endDate: string, product: string): Observable<any> {
        this.spinnerService.show('offerSpinner');

        const endpoint = environment.offer_equipment_url;

        const actionType: string = 'REMOVE';
        //const product: string = 'econ-dr';

        const body = {
            action_type: actionType,
            portfolio_id: portfolioId,
            level: equipmentLevel,
            equipment_id: equipmentId,
            start_datetime: startDate,
            end_datetime: endDate,
            product: product,
        };

        return this.data.post(endpoint, body, this.getHeader()).map((response) => {
            return {
                response: response,
                level: equipmentLevel,
                portfolioId: portfolioId,
                equipmentId: equipmentId,
                startDate: startDate,
                endDate: endDate
            };
        }).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }

    rejectEquipmentOrGroupFromPortfolio(portfolioId: string, equipmentLevel: string, equipmentId: string, startDate: string, endDate: string, product: string): Observable<any> {
        this.spinnerService.show('offerSpinner');

        const endpoint = environment.offer_equipment_url;

        const actionType: string = 'REJECT';
        //const product: string = 'econ-dr';

        const body = {
            action_type: actionType,
            portfolio_id: portfolioId,
            level: equipmentLevel,
            equipment_id: equipmentId,
            start_datetime: startDate,
            end_datetime: endDate,
            product: product,
        };

        return this.data.post(endpoint, body, this.getHeader()).map((response) => {
            return {
                response: response,
                level: equipmentLevel,
                portfolioId: portfolioId,
                equipmentId: equipmentId,
                startDate: startDate,
                endDate: endDate
            };
        }).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }

    moveBid(portfolioId: string, equipmentLevel: string, equipmentId: string, startDate: string, endDate: string, product: string, destProductId: number): Observable<any> {
        this.spinnerService.show('offerSpinner');

        const endpoint = environment.offer_equipment_url;

        const actionType: string = 'MOVE';

        const body = {
            action_type: actionType,
            portfolio_id: portfolioId,
            level: equipmentLevel,
            equipment_id: equipmentId,
            start_datetime: startDate,
            end_datetime: endDate,
            product: product,
            product_dest_id: destProductId
        };

        return this.data.post(endpoint, body, this.getHeader()).map((response) => {
            return {
                response: response,
                level: equipmentLevel,
                portfolioId: portfolioId,
                equipmentId: equipmentId,
                startDate: startDate,
                endDate: endDate
            };
        }).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }

    private setBodyForaddEquipmentToPortfolio(offerEntryData: OfferEntryData): any[] {
        const returnBody: any[] = [];

        const actionType: string = 'ADD'; // 'REMOVE'
        const portfolioId: string = offerEntryData.loadedPortfolioID;
        const startDatetime: string = this.getDateAsFormatedString(offerEntryData.startTime);
        const endDatetime: string = this.getDateAsFormatedString(offerEntryData.endTime);
        const product: string = offerEntryData.product;//'econ-dr';
        let equipmentId: string = '';
        let intervalId: number[] = [];
        let dataValue: number[] = [];

        for (const gridRow of offerEntryData.gridRows) {
            equipmentId = gridRow.id;
            const filteredDataRow = gridRow.dataRows.filter(row => row.metaDataKey === 'cust_est_data');
            if (filteredDataRow.length > 0) {
                const custEstData = filteredDataRow[0];
                for (let i = 0 ; i < custEstData.dataValues.length ; i++) {
                    if (custEstData.dataValues[i]) { // Todo: need to define null
                        dataValue.push(parseInt(custEstData.dataValues[i].toString()));
                        intervalId.push(offerEntryData.intervalIds[i]);
                    }
                }
            } else {
                console.error('Cannot Find Customers Estimate');
            }

            const body = {
                action_type: actionType,
                portfolio_id: portfolioId,
                equipment_id: equipmentId,
                start_datetime: startDatetime,
                end_datetime: endDatetime,
                product: product,
            };

            if (intervalId.length > 0 && intervalId.length === dataValue.length) {
                body['interval_id'] = intervalId;
                body['data_value'] = dataValue;
            }

            returnBody.push(body);
            equipmentId = '';
            intervalId = [];
            dataValue = [];
        }

        return returnBody;
    }

    private getDateAsFormatedString(date: any): string {
        if (date) {
            if (date instanceof Date) {
                const year: string = date.getFullYear().toString();
                const month: string = (date.getMonth() + 1) < 10 ? '0' + (date.getMonth() + 1) : (date.getMonth() + 1).toString();
                const day: string = (date.getDate() < 10) ? '0' + date.getDate() : date.getDate().toString();
                const hour: string = (date.getHours() < 10) ? '0' + date.getHours() : date.getHours().toString();
                const minute: string = (date.getMinutes() < 10) ? '0' + date.getMinutes() : date.getMinutes().toString();
                return `${year}-${month}-${day}T${hour}:${minute}`;
            } else {
                const year = date.date.year;
                const month = date.date.month.toLocaleString(undefined, {minimumIntegerDigits: 2, useGrouping: false});
                const day = date.date.day.toLocaleString(undefined, {minimumIntegerDigits: 2, useGrouping: false});
                return `${year}-${month}-${day}T00:00`;
            }
        }
        return '';
    }

    getEquipmentsForGroup(portfolioId: string, startDate: string, endDate: string): Observable<any> {
        this.spinnerService.show('offerSpinner');
        const endpoint =
            `${environment.equipment_url}/group?portfolio_id=${portfolioId}&start_datetime=${startDate}&end_datetime=${endDate}`;

        if (this.useMockData) {
            return Observable.of(this.getMockData()).map(data => {
                let returnData: any = null;
                if (Array.isArray(data) && data.length > 0) {
                    const equipmentList: Equipment[] = [];
                    for (const tmpEquipment of data) {
                        const equipment: Equipment = {
                            id: tmpEquipment.equipment_id,
                            displayedName: tmpEquipment.source_equipment_id,
                            sourceEquipmentId: tmpEquipment.source_equipment_id,
                            description: (tmpEquipment.equipment_description)? tmpEquipment.equipment_description : ''
                        };
                        equipmentList.push(equipment);
                    }

                    returnData = {
                        portfolioId: portfolioId,
                        startDatetime: startDate,
                        endDatetime: endDate,
                        data: equipmentList,
                        isGroup: true
                    };
                    this.equipments.next(returnData);
                } else {
                    this.alertService.error(this.translateService.instant('shared.add-registration.no-equipment-to-add'));
                }
                return returnData;
            }).finally(() => {
                this.spinnerService.hide('offerSpinner');
            });
        } else {
            return this.data.get(endpoint, this.getHeader()).map(data => {
                let returnData: any = null;
                let dataArray = this.getDataProperty(data);
                if (dataArray != null){//if (Array.isArray(data) && data.length > 0) {
                    const equipmentList: Equipment[] = [];
                    for (const tmpEquipment of dataArray) {
                        const equipment: Equipment = {
                            id: tmpEquipment.equipment_id,
                            displayedName: tmpEquipment.source_equipment_id,
                            sourceEquipmentId: tmpEquipment.source_equipment_id,
                            description: (tmpEquipment.equipment_description)? tmpEquipment.equipment_description : ''
                        };
                        equipmentList.push(equipment);
                    }

                    returnData = {
                        portfolioId: portfolioId,
                        startDatetime: startDate,
                        endDatetime: endDate,
                        data: equipmentList,
                        isGroup: true
                    };
                    this.equipments.next(returnData);
                } else {
                    this.alertService.error(this.translateService.instant('shared.add-registration.no-equipment-to-add'));
                }
                return returnData;
            }).finally(() => {
                this.spinnerService.hide('offerSpinner');
            });
        }
    }

    getEquipmentsForPortfolio(portfolioId: string, startDate: string, endDate: string, product: string): Observable<any> {
        this.spinnerService.show('offerSpinner');
        const endpoint = `${environment.equipment_url}?portfolio_id=${portfolioId}&start_datetime=${startDate}&end_datetime=${endDate}&product=${product}`;

        if (this.useMockData) {
            return Observable.of(this.getMockData()).map(data => {
                let returnData: any = null;
                if (Array.isArray(data) && data.length > 0) {
                    const equipmentList: Equipment[] = [];
                    for (const tmpEquipment of data) {
                        const equipment: Equipment = {
                            id: tmpEquipment.equipment_id,
                            displayedName: tmpEquipment.source_equipment_id,
                            sourceEquipmentId: tmpEquipment.source_equipment_id,
                            description: (tmpEquipment.equipment_description) ? tmpEquipment.equipment_description : ''
                        };
                        equipmentList.push(equipment);
                    }

                    returnData = {
                        portfolioId: portfolioId,
                        startDatetime: startDate,
                        endDatetime: endDate,
                        data: equipmentList,
                        product: product,
                        isGroup: false
                    };
                    this.equipments.next(returnData);
                } else {
                    this.alertService.error(this.translateService.instant('shared.add-registration.no-equipment-to-add'));
                }
                return returnData;
            }).finally(() => {
                this.spinnerService.hide('offerSpinner');
            });
        }

        return this.data.get(endpoint, this.getHeader()).map(data => {
            let returnData: any = null;
            let dataArray = this.getDataProperty(data);
            if (dataArray != null){
                const equipmentList: Equipment[] = [];
                for (const tmpEquipment of dataArray) {
                    const equipment: Equipment = {
                        id: tmpEquipment.equipment_id,
                        displayedName: tmpEquipment.source_equipment_id,
                        sourceEquipmentId: tmpEquipment.source_equipment_id,
                        description: (tmpEquipment.equipment_description)? tmpEquipment.equipment_description : ''
                    };
                    equipmentList.push(equipment);
                }

                returnData = {
                    portfolioId: portfolioId,
                    startDatetime: startDate,
                    endDatetime: endDate,
                    data: equipmentList,
                    product: product,
                    isGroup: false
                };
                this.equipments.next(returnData);
            } else {
                this.alertService.error(this.translateService.instant('shared.add-registration.no-equipment-to-add'));
            }
            return returnData;
        }).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }

    loadData() {
        this.spinnerService.show('offerSpinner');
        const endpoint = `${environment.equipment_url}/grouping/v1`;

        this.data.get(endpoint, this.getHeader()).finally(() => {
            this.spinnerService.hide('offerSpinner');
        }).subscribe(data => {
            //if (Array.isArray(data) && data.length > 0) {
            let dataArray = this.getDataProperty(data);
            if (dataArray != null){
                // Portfolio List
                const portfolioList: TreeNode[] = [];
                const nonGroupedEquipmentList = [];
                for (const item of dataArray) {
                    const portfolioData: any = {
                        id: item.id,
                        code: item.code,
                        name: item.display_name,
                        nameEn: item.name_en,
                        nameLocal: item.name_local,
                        aggregationlevel: item.level
                    };

                    const groupList: TreeNode[] = [];
                    if (item.groups && item.groups.length > 0) {
                        for (const subItem of item.groups) {
                            const groupData = {
                                id: subItem.id,
                                code: subItem.code,
                                name: subItem.display_name,
                                nameEn: subItem.name_en,
                                nameLocal: subItem.name_local,
                                deratingFactor: subItem.derating_factor,
                                aggregationlevel: subItem.level,
                                startDatetime: subItem.start_date,
                                endDatetime: subItem.end_date
                            };

                            const equipmentList: TreeNode[] = [];
                            if (subItem.registrations && subItem.registrations.length > 0) {
                                for (const terminalNode of subItem.registrations) {
                                    const equipment: TreeNode =  {
                                        data: {
                                            id: terminalNode.id,
                                            code: terminalNode.code,
                                            name: terminalNode.display_name,
                                            nameEn: terminalNode.name_en,
                                            nameLocal: terminalNode.name_local,
                                            deratingFactor: terminalNode.derating_factor,
                                            aggregationlevel: terminalNode.level
                                        }
                                    };
                                    equipmentList.push(equipment);
                                }
                            }
                            const group: TreeNode = {
                                data: groupData,
                                children: equipmentList
                            };
                            groupList.push(group);
                        }
                    }
                    const portfolio: TreeNode = {
                        data: portfolioData,
                        children: groupList
                    };
                    portfolioList.push(portfolio);

                    const nonGroupedEquipmentInPortfolio: Equipment[] = [];
                    if (item.registrations && item.registrations.length > 0) {
                        for (const subItem of item.registrations) {
                            // Non Grouped Equipment
                            const equipment: Equipment = {
                                id: subItem.id,
                                code: subItem.code,
                                displayedName: subItem.display_name,
                                nameEn: subItem.name_en,
                                nameLocal: subItem.name_local,
                            };
                            nonGroupedEquipmentInPortfolio.push(equipment);
                        }
                    }
                    const portfolioNonGrouped: any = {
                        id: item.id,
                        name: item.display_name,
                        nameEn: item.name_en,
                        nameLocal: item.name_local,
                        aggregationlevel: item.level,
                        equipment: nonGroupedEquipmentInPortfolio
                    };
                    nonGroupedEquipmentList.push(portfolioNonGrouped);
                }
                this.utilsService.debugLog('portfolioList', portfolioList);
                this.utilsService.debugLog('non-grouped-portfolioList', nonGroupedEquipmentList);
                this.groups.next(portfolioList);
                this.nonGroupedEquipment.next(nonGroupedEquipmentList);
            } else {
                this.alertService.error(this.translateService.instant('shared.add-registration.no-equipment-to-add'));
            }
        });
    }

    getEquipments(): Observable<any> {
        return this.equipments.asObservable();
    }

    getNonGroupedEquipment(): Observable<any> {
        return this.nonGroupedEquipment.asObservable();
    }

    getGroupList(): Observable<any> {
        if (this.useMockData) {
            return Observable.of(this.getGroupMockData()).map(data => {
                return data;
            }).finally(() => {
                this.spinnerService.hide('offerSpinner');
            });
        } else {
            return this.groups.asObservable();
        }
    }

    getDataProperty(wrapper: any): any[] {
        let returnData: any = null;
        if (wrapper['data'] != null && Array.isArray(wrapper['data']) && wrapper['data'].length > 0) {
            return wrapper['data'];
        }
        return returnData;
    }

    getMockData(): any[] {
        return [
            {'equipment_id': 'E1', 'equipment_description': '0123456789', 'checked': false},
            {'equipment_id': 'E2', 'equipment_description': '0526315068', 'checked': false},
            {'equipment_id': 'E3', 'equipment_description': '1020142603', 'checked': false},
            {'equipment_id': 'E4', 'equipment_description': '0631473787', 'checked': false},
            {'equipment_id': 'E5', 'equipment_description': '0534517870', 'checked': false},
            {'equipment_id': 'E6', 'equipment_description': '0431837747', 'checked': false},
            {'equipment_id': 'E7', 'equipment_description': '1216119134', 'checked': false},
            {'equipment_id': 'E8', 'equipment_description': '0227454484', 'checked': false},
            {'equipment_id': 'E9', 'equipment_description': '0227445422', 'checked': false},
            {'equipment_id': 'E10', 'equipment_description': '1713385324', 'checked': false},
            {'equipment_id': 'E11', 'equipment_description': '0632570288', 'checked': false},
            {'equipment_id': 'E12', 'equipment_description': '0622235311', 'checked': false},
            {'equipment_id': 'E13', 'equipment_description': '0526315059', 'checked': false},
            {'equipment_id': 'E14', 'equipment_description': '0917106400', 'checked': false},
            {'equipment_id': 'E15', 'equipment_description': '0333863270', 'checked': false},
            {'equipment_id': 'E16', 'equipment_description': '0526315095', 'checked': false},
            {'equipment_id': 'E17', 'equipment_description': '1316119481', 'checked': false},
            {'equipment_id': 'E18', 'equipment_description': '0227442700', 'checked': false},
            {'equipment_id': 'E19', 'equipment_description': '0422203706', 'checked': false}
        ];
    }

    getGroupMockData(): any[] {
        return [
            {
                'data': {'id': '1', 'name': 'ENO-18I-C02', 'aggregationlevel': 'PORTFOLIO'},
                'children': []
            },
            {
                'data': {'id': '2', 'name': 'ENO-18I-N03', 'aggregationlevel': 'PORTFOLIO'},
                'children': []
            },
            {
                'data': {'id': '3', 'name': 'ENO-18I-C01', 'aggregationlevel': 'PORTFOLIO'},
                'children': []
            },
            {
                'data': {'id': '4', 'name': 'ENO-18I-N02', 'aggregationlevel': 'PORTFOLIO'},
                'children': [
                    {
                        'data': {'id': 'G1', 'name': 'Sampyo Cement - Samcheok Factory', 'aggregationlevel': 'GROUP'},
                        'children': [
                            {
                                'data': {'id': 'E122', 'name': '1714720497', 'aggregationlevel': 'REGISTRATION'}
                            },
                            {
                                'data': {'id': 'E121', 'name': '1714720530', 'aggregationlevel': 'REGISTRATION'}
                            }
                        ]
                    }
                ]
            },
            {
                'data': {'id': '5', 'name': 'ENO-18I-N01', 'aggregationlevel': 'PORTFOLIO'},
                'children': []
            }
        ];
    }
}



// WEBPACK FOOTER //
// ./src/app/core/services/equipment.service.ts